<template>
  <div v-if="active" class="sp-alert" :class="classModifiers">
    <div class="sp-alert__prepend" part="prepend">
      <slot name="prepend">
        <sp-icon v-if="icon" :key="String(iconFillColor)" :name="icon" :fill-color="iconFillColor"></sp-icon>
      </slot>
    </div>

    <div class="sp-alert-content" part="content">
      <slot>
        {{ text }}
      </slot>
    </div>
    <div class="sp-alert__append" part="append">
      <slot name="append"></slot>
    </div>
    <div class="sp-alert__close" part="close" @click="onCloseRegionClick">
      <slot name="close">
        <sp-button
          v-if="closeable"
          class="sp-alert__close-button"
          variant="text"
          size="xx-small"
          :aria-label="tAccessibleName('alert_dismiss')"
          @click.stop="dismiss"
        >
          <sp-icon name="close"></sp-icon>
        </sp-button>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { watchImmediate } from "@vueuse/core";
import { computed, ref, useHost } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { isPresent, toBoolean } from "#ce/utils/props";
import { useAlertDismissal } from "./composables/useAlertDismissal";
import { useFlashSingleton } from "./composables/useFlashSingleton";
import { props as alertProps } from "./props.js";

const emit = defineEmits(["close"]);

const props = defineProps(alertProps);

const active = ref(true);
const closeable = computed(() => toBoolean(props.closeable));

// -color variants, so each type reads at a glance. They carry their own palette, which is
// why the prepend slot's forced fill applies to ::slotted icons only.
const icons = {
  success: "check-color",
  info: "info-color",
  warning: "warning-color",
  error: "important-color",
};

// A string that is not the attribute's own on/off spelling names the glyph; otherwise presence
// selects the one matching the type, so a bare `icon` attribute turns it on.
const icon = computed(() => {
  const requested = props.icon;

  if (typeof requested === "string" && !["", "true", "false"].includes(requested)) {
    return requested;
  }

  return isPresent(requested) ? icons[props.type] : false;
});

const { isSmScreen, breakpointModifiers } = useBreakpoints();
const host = useHost();

watchImmediate(isSmScreen, (value) => {
  host?.classList.toggle("--is-sm-screen", value);
});

const classModifiers = computed(() => [
  ...breakpointModifiers.value,
  {
    [`--variant-${props.variant}`]: props.variant,
    [`--type-${props.type}`]: props.type,
    [`--append-align-${props.appendAlign}`]: props.appendAlign,
    "--compact": toBoolean(props.compact),
    "--fixed": toBoolean(props.fixed),
    "--flash": toBoolean(props.flash),
    "--closeable": closeable.value,
  },
]);

function close() {
  if (!active.value) {
    return;
  }

  active.value = false;
  emit("close");
}

const { dismiss } = useAlertDismissal(props, host, close);

useFlashSingleton(props, host);

// The region spans the close slot, so a slotted close control dismisses through it.
function onCloseRegionClick() {
  if (closeable.value) {
    dismiss();
  }
}
</script>

<style>
:host {
  --alert-background-color: var(--sp-comp-alert-background-color, var(--sp-sys-color-surface, #ffffff));
  --alert-text-color: var(--sp-comp-alert-text-color, var(--sp-sys-color-on-surface, #1e293b));
  --alert-info-background-color: var(--sp-comp-alert-info-background-color, #eef4f5);
  --alert-info-text-color: var(--sp-comp-alert-info-text-color, var(--sp-sys-color-on-surface, #1e293b));
  --alert-info-link-color: var(--sp-comp-alert-info-link-color, var(--sp-sys-color-link, #007078));
  --alert-warning-background-color: var(--sp-comp-alert-warning-background-color, #fff8e8);
  --alert-warning-text-color: var(--sp-comp-alert-warning-text-color, var(--sp-sys-color-on-surface, #1e293b));
  --alert-warning-link-color: var(--sp-comp-alert-warning-link-color, var(--sp-sys-color-link, #007078));
  --alert-error-background-color: var(--sp-comp-alert-error-background-color, #f1d7d9);
  --alert-error-text-color: var(--sp-comp-alert-error-text-color, var(--sp-sys-color-on-surface, #1e293b));
  --alert-error-link-color: var(--sp-comp-alert-error-link-color, var(--sp-sys-color-link, #007078));
  --alert-success-background-color: var(--sp-comp-alert-success-background-color, #e6f6ea);
  --alert-success-text-color: var(--sp-comp-alert-success-text-color, var(--sp-sys-color-on-surface, #1e293b));
  --alert-success-link-color: var(--sp-comp-alert-success-link-color, var(--sp-sys-color-link, #007078));
  --alert-border-radius: var(--sp-comp-alert-border-radius, 0.3125rem);
  --alert-border: var(--sp-comp-alert-border, none);
  --alert-content-line-height: var(--sp-comp-alert-content-line-height, 1.5);
  --alert-display: var(--sp-comp-alert-display, grid);
  --alert-prepend-icon-size: var(--sp-comp-alert-prepend-icon-size, 55px);
  --alert-append-icon-size: var(--sp-comp-alert-append-icon-size, 22px);
  --alert-append-icon-fill: var(--sp-comp-alert-append-icon-fill, var(--sp-sys-color-link));
  --alert-column-gap: var(--sp-comp-alert-column-gap, var(--sp-ref-scale-8));

  display: var(--alert-display);
  box-sizing: border-box;
}
:host([compact]) {
  --alert-display: inline-grid;
}
:host(.--is-sm-screen[compact]) {
  --alert-display: grid;
}
/* Opacity only: :host([flash]) centres itself with a transform, which animating
   transform here would override. */
@keyframes sp-alert-dismiss {
  to {
    opacity: 0;
  }
}

:host(.--is-dismissing) {
  animation: sp-alert-dismiss var(--sp-comp-alert-dismiss-duration, 200ms) ease-in forwards;
}

:host([flash]) {
  z-index: var(--sp-ref-z-index-max, 99999);
  position: fixed;
  top: var(--sp-ref-scale-48);
  left: 50%;
  transform: translateX(-50%);
  display: grid;
}
:host(.--is-sm-screen[flash]) {
  top: var(--sp-ref-scale-8);
  width: max-content;
  max-width: calc(100% - var(--sp-ref-scale-16));
}
</style>

<style lang="scss" scoped>
@use "sp-alert-content";

.sp-alert {
  --alert-append-align: var(--sp-comp-alert-append-align, flex-start);
  --alert-append-column-gap: 0;
  --alert-padding: var(--sp-comp-alert-padding, var(--sp-ref-scale-24));
  --alert-content-font-family: var(--sp-comp-alert-content-font-family, var(--sp-sys-font-family-normal));
  --alert-content-font-weight: var(--sp-comp-alert-content-font-weight, var(--sp-sys-font-weight-normal));
  --alert-content-font-size: var(--sp-comp-alert-content-font-size, var(--sp-ref-font-size-medium));

  display: var(--alert-display);
  padding: var(--alert-padding);
  position: relative;
  background-color: var(--alert-background-color);
  color: var(--alert-text-color);
  box-shadow: var(--alert-box-shadow, none);
  box-sizing: border-box;
  border: var(--alert-border);
  border-radius: var(--alert-border-radius);
  grid-template-areas: "prepend content append close";
  grid-template-columns: max-content auto max-content max-content;
  row-gap: var(--alert-column-gap);

  &.--fixed {
    position: fixed;
  }

  @each $type in ("info", "warning", "error", "success") {
    &.--type-#{$type} {
      --alert-background-color: var(--alert-#{$type}-background-color);
      --alert-text-color: var(--alert-#{$type}-text-color);

      &.--variant-text {
        --alert-text-color: var(--sp-sys-color-#{type}, var(--alert-#{$type}-text-color));
      }

      // Only a consumer's own glyph takes the alert's text colour; the built-in type icon
      // is a -color variant that carries its own palette.
      .sp-alert__prepend ::slotted(sp-icon) {
        --sp-comp-icon-fill: var(--sp-comp-alert-#{$type}-prepend-icon-fill, var(--alert-text-color));
      }
    }
  }

  &.--variant-elevated {
    z-index: var(--sp-comp-alert-elevated-z-index, 10);
    --alert-box-shadow: var(--sp-comp-alert-elevated-box-shadow, 0 2px 10px 0 rgba(0, 0, 0, 0.25));
  }

  &.--variant-text {
    --alert-background-color: transparent;
    --alert-padding: 0;
    --alert-border: none;
    --alert-border-radius: 0;
    --alert-content-line-height: 1.2;
    --alert-display: inline-grid;
    --alert-prepend-icon-size: 1em;
    --alert-append-icon-size: 1em;
  }

  &.--append-align-center {
    --alert-append-align: center;
  }

  &.--append-align-end {
    --alert-append-align: flex-end;
  }

  &.--compact {
    --alert-padding: var(--sp-comp-alert-compact-padding, var(--sp-ref-scale-12));
    --alert-prepend-icon-offset: 0;
    --alert-prepend-icon-size: var(--sp-sys-icon-size-small);
    --alert-append-icon-size: calc(var(--sp-sys-icon-size-small) - 2);
    --alert-column-gap: 0.5ch;
    --alert-content-font-family: var(--sp-comp-alert-compact-content-font-family, var(--sp-sys-font-family-medium));
    --alert-content-font-weight: var(--sp-comp-alert-compact-content-font-weight, var(--sp-sys-font-weight-medium));
    --alert-content-font-size: var(--sp-comp-alert-compact-content-font-size, 0.9375rem);
    --alert-append-align: center;
    --alert-border-radius: var(--sp-comp-alert-compact-border-radius, var(--sp-ref-border-radius-xs));
    justify-content: center;
  }

  &.--closeable {
    .sp-alert-content {
      padding-inline-end: calc(var(--sp-sys-icon-size-medium) / 2);
    }

    .sp-alert__close {
      cursor: pointer;
    }
  }
}

.sp-alert__prepend {
  align-self: flex-start;
  align-items: center;
  display: flex;
  grid-area: prepend;
  // One content line tall, so the icon centres on the first text line whatever
  // the two boxes' sizes; a themed line-height with a unit invalidates the calc
  // and the height falls back to auto.
  height: calc(var(--alert-content-font-size) * var(--alert-content-line-height));

  ::slotted(sp-icon) {
    --font-size: var(--alert-prepend-icon-font-size, 1rem);
    margin-inline-end: var(--alert-column-gap);
    --size: var(--alert-prepend-icon-size);
    margin-left: var(--alert-prepend-icon-offset, -8px);
    --fill: red;
  }
}

.sp-alert__append {
  align-self: var(--alert-append-align);
  align-items: center;
  display: flex;
  grid-area: append;

  /* FIXME: make this selector work */
  ::slotted(*) {
    margin-inline-start: var(--alert-append-column-gap);
  }

  ::slotted(sp-icon) {
    --size: var(--alert-append-icon-size);
    --fill: var(--alert-append-icon-fill);
  }
}

.sp-alert__close {
  --offset: calc(var(--sp-ref-scale-16) * -1);
  position: absolute;
  align-self: flex-start;
  align-items: center;
  display: flex;
  grid-area: close;
  left: var(--offset);
  top: var(--offset);

  ::slotted(sp-icon) {
    margin-inline-start: var(--alert-column-gap);
    opacity: 0.3;
    margin-right: calc(var(--sp-ref-scale-8) * -1);
  }
}
</style>
